/**
 * 260930 Android 네이티브 송출 transport 용 ICE 재시작 어댑터.
 *
 * 왜 따로 있나: Android 는 송출 transport 객체를 네이티브가 쥐고 있어 JS 에 transport 가 없다.
 *          그래서 PC 의 createSendIceRestart 처럼 transport.on("connectionstatechange") 를 들을 수도,
 *          transport.restartIce() 를 부를 수도 없다. 판단(iceRestartCoordinator)은 그대로 한 벌 쓰고,
 *          상태는 네이티브가 알려 주고(pro-producerTransportState), 적용·정리는 네이티브에 시킨다.
 *
 * 세대는 transportId 로 가른다. 이 어댑터는 transport 하나(= id 하나)에 하나씩 만들고,
 * 새 transport 를 만들면 옛것을 retire() 한 뒤 새로 만든다.
 *
 * 이 모듈은 판단과 배선만 한다 — 전역도 import 도 조정자 말고는 없다. 부작용은 전부 주입된 콜백이다.
 * (합의: 프론트 web-frontend-native-producer-restart-recheck-20260930.md §3 ·
 *        Android android-pro270-answers-restore-flag-transportid-20260930.md §2)
 */
import { createIceRestartCoordinator } from "./iceRestartCoordinator";

/**
 * 261027 웹이 포기하기 전에 네이티브가 스스로 정리했다(유예 15초 만료) — 그 closed 를 받고 포기할 때의 이유.
 * ⚠ 합의 목록(iceRestartCoordinator REASON · connect-timeout)에 더한 이름이다. Android 가 받기로 했다
 * (android-answer-round95-frontend-camera-and-agent-fgs-20261027.md §1).
 */
export const NATIVE_CLOSED_REASON = "native-closed";

/**
 * @param {Object}   deps
 * @param {String}   deps.transportId   - 서버가 준 송출 transport id (네이티브 transport.id 와 같다)
 * @param {Function} deps.restartAfter  - (iceParametersJson, transportId) → 네이티브 restartIce 적용
 * @param {Function} deps.restartFailed - (reason, transportId) → 네이티브 transport 정리(캡처는 유지)
 * @param {Function} deps.sendRequest   - (transportId) → 서버에 live_restartProducerTransport 요청
 * @param {Function} deps.recreate      - () → 서버에 live_createProducerTransport 요청(참여 때와 같은 길)
 */
export const createAndroidSendIceRestart = ({ transportId, restartAfter, restartFailed, sendRequest, recreate }) => {
    let timer = null;
    // 포기했거나 새 transport 로 갈아 끼웠다. 그 뒤로는 무엇이 와도 아무것도 하지 않는다.
    let retired = false;

    const clearWake = () => {
        if (timer !== null) {
            clearTimeout(timer);
            timer = null;
        }
    };

    // 포기는 이 transport 에 한 번뿐이다. 조정자의 fallback 과 바깥의 abandon(재생성 감시 만료)이 함께 쓴다.
    const giveUp = (reason) => {
        if (retired) return false;
        retired = true;
        clearWake();
        console.log(`androidSendIceRestart - 포기(${reason}) transportId[${transportId}] — 네이티브 정리 뒤 다시 세운다`);
        // ⚠ 순서가 계약이다. 네이티브는 정리와 생성을 같은 workHandler 에 줄 세운다 —
        // 정리를 먼저 부르고 생성 요청을 뒤에 보내야 정리가 앞선다(Android 회신 09-30 §3).
        restartFailed(reason, transportId);
        recreate();
        return true;
    };

    const coordinator = createIceRestartCoordinator({
        sendRequest : () => sendRequest(transportId),
        // 네이티브 적용은 동기 브릿지 호출이다. 붙었는지는 네이티브의 connected 로 안다(정착 타이머).
        // 브릿지가 던지면 Promise 가 거절돼 조정자가 failed-response 로 포기한다.
        restartIce  : (iceParameters) => {
            restartAfter(JSON.stringify(iceParameters), transportId);
            return Promise.resolve();
        },
        fallback    : (reason) => {
            giveUp(reason);
        },
        setWake     : (ms) => {
            clearWake();
            timer = setTimeout(() => {
                timer = null;
                if (retired) return;
                coordinator.onWake();
            }, ms);
        },
        clearWake
    });

    return {
        transportId,

        /** 네이티브 pro-producerTransportState. 다른 transport 의 것은 버린다. */
        onNativeState: (state, stateTransportId) => {
            if (retired) return;
            if (stateTransportId !== transportId) {
                console.log(`androidSendIceRestart - 다른 transport 의 상태를 버린다. state[${state}] transportId[${stateTransportId}] 지금[${transportId}]`);
                return;
            }
            // 261027 Pro 95-3: 웹 타이머가 돌지 않아 네이티브 유예가 먼저 만료됐다. 09-30 에는 「유예 만료 뒤 복구는
            // 재로그인 경로가 맡는다」고 봤지만 짧은 끊김은 MQ 가 스스로 살아나 재로그인이 오지 않는다 — 아무도 안 세운다.
            // 그래서 여기서 합의된 쌍(포기 신호 → 생성)을 보낸다. 네이티브는 이미 정리됐어도 같은 id 면 복원 표시만 세우고,
            // 캡처는 쥐고 있어 복원 produce 가 된다(Android 95차 답 §1·§2).
            // ⚠ 네이티브는 transport 가 닫힐 때마다 closed 를 보낸다(공유 종료·정리 포함). 유예는 disconnected·failed 에서만
            // 시작되므로, 끊김을 받아 복구 중일 때(조정자가 idle 이 아님)의 closed 만 유예 만료로 본다. 붙어 있다가 닫힌 것은 그대로 둔다.
            if (state === "closed" && coordinator.getPhase() !== "idle") {
                giveUp(NATIVE_CLOSED_REASON);
                return;
            }
            coordinator.onState(state);
        },

        /** 서버 live_restartProducerTransport 응답 */
        onResponse: (rtnVal) => {
            if (retired) return Promise.resolve();
            return coordinator.onResponse(rtnVal, transportId);
        },

        /**
         * 261002 바깥에서 이 transport 를 포기시킨다 — 재생성한 transport 가 connecting 에서 멈춰
         * 조정자를 깨울 이벤트가 없을 때(sendRecreateWatch 만료). 포기했으면 true, 이미 멈췄으면 false.
         */
        abandon: (reason) => giveUp(reason),

        /** 새 transport 로 갈아 끼울 때 옛것을 멈춘다 */
        retire: () => {
            retired = true;
            clearWake();
            coordinator.reset();
        }
    };
}
